import { BIRTHDATE_FIELD, makeBirthdateSystemToday } from "./birthdateWire";
import { fieldTypeForProperty } from "../../segment/utils/unomiConditionUtils";
import {
    fieldMetaFromSchemaNode,
    inferValueKindFromFieldName,
    schemaNodeToValueKind,
    valueKindLabel,
    widgetForValueKind,
} from "./fieldTypeRegistry";
import { getPayloadSchemaProperties } from "./payloadSchemaManual";

const VALUE_KIND_RANK = {
    string: 10,
    array: 15,
    object: 15,
    number: 20,
    boolean: 20,
    enum: 25,
    email: 40,
    phone: 40,
    uri: 40,
    uuid: 40,
    ipv4: 40,
    time: 40,
    date: 40,
    datetime: 40,
};

function pickRicherValueKind(current, incoming) {
    if (!incoming) return current;
    if (!current) return incoming;
    return (VALUE_KIND_RANK[incoming] ?? 0) >= (VALUE_KIND_RANK[current] ?? 0) ? incoming : current;
}

function lookupPayloadSchemaProperty(catalog, shortName) {
    if (!shortName) return null;
    const props = getPayloadSchemaProperties(catalog?.payload_schema);
    return props[shortName] || null;
}

function schemaNodeForPayloadField(pf, catalog) {
    const shortName = (pf.name || pf.path || pf.earnPointsPath || "").replace(/^payload\./, "");
    const fromSchema = lookupPayloadSchemaProperty(catalog, shortName);
    return {
        type: pf.type || fromSchema?.type,
        format: pf.format || fromSchema?.format,
        enum: pf.enum || fromSchema?.enum,
        description: pf.description || fromSchema?.description,
    };
}

const LOYALTY_NUMERIC_PATTERNS = [/\.metrics\./, /_count/, /_amount/, /_total/, /_score/, /_days/, /_age/, /_points/, /_value/];

const LOYALTY_NUMERIC_FIELDS = new Set([
    "customer.lifetime_points", "customer.status_points", "customer.birthdate_month",
    "customer.birthdate_day", "customer.created_at_month", "customer.created_at_day",
    "system.today_month", "system.today_day", "system.customer_created_days",
    "system.customer_last_activity_days",
]);

const LOYALTY_DATE_FIELDS = new Set([
    "customer.last_activity_at", "customer.created_at", "system.now",
]);

const LOYALTY_ENUM_FIELDS = {
    "customer.gender": ["M", "F", "OTHER", "UNKNOWN"],
    gender: ["M", "F", "OTHER", "UNKNOWN"],
    "customer.status": ["ACTIVE", "INACTIVE"],
    status: ["ACTIVE", "INACTIVE"],
};

const LOYALTY_SPECIAL = {
    loyaltyStatus: "loyalty_tier",
    "customer.loyalty_status": "loyalty_tier",
    loyalty_status: "loyalty_tier",
    rewards: "reward",
    "customer.rewards": "reward",
};

/** Métadonnées client si l'API ne les renvoie pas encore (parité segments / jobs internes). */
const CUSTOMER_FIELD_META_FALLBACK = {
    "customer.status": {
        valueKind: "enum",
        ui: { widget: "select", options: ["ACTIVE", "INACTIVE"] },
    },
    "customer.status_points": { valueKind: "number", ui: { widget: "number" } },
    "customer.birthdate": { valueKind: "date", ui: { widget: "date" } },
    "customer.created_at": { valueKind: "datetime", ui: { widget: "datetime" } },
    "customer.last_activity_at": { valueKind: "datetime", ui: { widget: "datetime" } },
    "customer.metrics.last_transaction_at": { valueKind: "datetime", ui: { widget: "datetime" } },
    "customer.metrics.transactions_count_30d": { valueKind: "number", ui: { widget: "number" } },
    "customer.metrics.transactions_count_90d": { valueKind: "number", ui: { widget: "number" } },
};

const LOYALTY_OP_LABELS = {
    eq: "= (égal)",
    neq: "≠ (différent)",
    gt: "> (supérieur)",
    gte: "≥ (sup. ou égal)",
    lt: "< (inférieur)",
    lte: "≤ (inf. ou égal)",
    between: "Entre [min, max]",
    in: "Dans la liste",
    contains: "Contient",
    exists: "Existe",
};

const UNOMI_OP_LABELS = {
    equals: "égal à",
    notEquals: "différent de",
    greaterThan: "supérieur à",
    greaterThanOrEqualTo: "supérieur ou égal à",
    lessThan: "inférieur à",
    lessThanOrEqualTo: "inférieur ou égal à",
    startsWith: "commence par",
    endsWith: "se termine par",
    contains: "contient",
    in: "dans la liste",
    between: "entre",
    exists: "existe",
};

/** Pools par type — inspirés des query builders (scalaires comparables, discrets, texte). */
const STRING_LIKE_KINDS = new Set(["string", "email", "phone", "uri", "uuid", "ipv4"]);

const LOYALTY_OPS_BY_KIND = {
    boolean: ["eq", "neq", "exists"],
    number: ["eq", "neq", "gt", "gte", "lt", "lte", "between", "in", "exists"],
    date: ["eq", "neq", "gt", "gte", "lt", "lte", "between", "exists"],
    datetime: ["eq", "neq", "gt", "gte", "lt", "lte", "between", "exists"],
    time: ["eq", "neq", "gt", "gte", "lt", "lte", "between", "exists"],
    enum: ["eq", "neq", "in", "exists"],
    string: ["eq", "neq", "contains", "in", "exists"],
    birthdate: ["eq", "neq", "exists"],
    loyalty_tier: ["eq", "neq", "in", "exists"],
    reward: ["eq", "neq", "in", "exists"],
    array: ["eq", "neq", "contains", "in", "exists"],
    object: ["exists"],
};

const UNOMI_OPS_BY_KIND = {
    boolean: ["equals", "notEquals", "exists"],
    number: ["equals", "notEquals", "greaterThan", "greaterThanOrEqualTo", "lessThan", "lessThanOrEqualTo", "between", "in", "exists"],
    date: ["equals", "notEquals", "greaterThan", "greaterThanOrEqualTo", "lessThan", "lessThanOrEqualTo", "between", "exists"],
    datetime: ["equals", "notEquals", "greaterThan", "greaterThanOrEqualTo", "lessThan", "lessThanOrEqualTo", "between", "exists"],
    enum: ["equals", "notEquals", "in", "exists"],
    string: ["equals", "notEquals", "startsWith", "endsWith", "contains", "in", "exists"],
    array: ["equals", "notEquals", "contains", "in", "exists"],
    object: ["exists"],
};

function normalizeSchemaNode(node) {
    if (!node || typeof node !== "object" || Array.isArray(node)) return node;
    const looksLikeManualMap = (
        !("type" in node)
        && !("properties" in node)
        && Object.entries(node).some(([k, v]) => (
            typeof k === "string" && k && v && typeof v === "object" && ("type" in v || "description" in v)
        ))
    );
    if (looksLikeManualMap) return { type: "object", properties: node };
    return node;
}

function schemaTypeToValueKind(node = {}) {
    return schemaNodeToValueKind(node);
}

function fieldMetaFromSchemaNodeLocal(node = {}) {
    return fieldMetaFromSchemaNode(node);
}

function mapPayloadTypeToValueKind(type, format) {
    return schemaNodeToValueKind({ type, format });
}

/** Extrait fieldMeta payload.* depuis payload_schema (manuel ou JSON Schema). */
export function buildFieldMetaFromPayloadSchema(schema, prefix = "payload") {
    const meta = {};
    if (!schema || typeof schema !== "object") return meta;

    const walk = (node, path, depth = 0) => {
        if (depth > 12 || !node || typeof node !== "object") return;
        const nodeType = node.type;
        if (nodeType === "object" || node.properties) {
            const props = node.properties;
            if (props && typeof props === "object") {
                Object.entries(props).forEach(([k, v]) => {
                    if (!k || typeof k !== "string") return;
                    const nextPath = path ? `${path}.${k}` : k;
                    if (v && typeof v === "object") {
                        const childType = v.type;
                        if (childType === "object" || v.properties) {
                            meta[nextPath] = { valueKind: "string", ui: { widget: "text" } };
                            walk(v, nextPath, depth + 1);
                        } else if (childType === "array") {
                            meta[nextPath] = fieldMetaFromSchemaNodeLocal(v);
                        } else {
                            meta[nextPath] = fieldMetaFromSchemaNodeLocal(v);
                        }
                    }
                });
            }
            return;
        }
        if (nodeType === "array" && node.items) walk(node.items, path, depth + 1);
    };

    walk(normalizeSchemaNode(schema), prefix);
    return meta;
}

/** Identifiant canonique d'un champ (items string ou objet { id, type, … }). */
export function catalogFieldId(item) {
    if (!item) return "";
    if (typeof item === "string") return item;
    return item.id || item.name || item.field || item.path || item.earnPointsPath || "";
}

function mergeFieldMetaEntry(existing, incoming) {
    if (!incoming || typeof incoming !== "object") return existing || {};
    const next = { ...(existing || {}) };
    const fromType = incoming.type
        ? mapPayloadTypeToValueKind(incoming.type, incoming.format)
        : null;
    const vk = incoming.valueKind || incoming.value_kind;
    next.valueKind = pickRicherValueKind(
        next.valueKind,
        pickRicherValueKind(vk, fromType),
    );
    if (incoming.format) next.format = incoming.format;
    if (incoming.label && !next.label) next.label = incoming.label;
    if (incoming.description && !next.label) next.label = incoming.description;
    if (Array.isArray(incoming.operators) && incoming.operators.length && !next.operators?.length) {
        next.operators = [...incoming.operators];
    }
    if (incoming.ui) {
        const mergedUi = { ...(next.ui || {}), ...incoming.ui };
        if (next.valueKind && !mergedUi.widget) {
            mergedUi.widget = widgetForValueKind(next.valueKind);
        }
        next.ui = mergedUi;
    } else if (next.valueKind && !next.ui?.widget) {
        next.ui = { ...(next.ui || {}), widget: widgetForValueKind(next.valueKind) };
    }
    return next;
}

/**
 * Enrichit le catalogue API côté frontend (sans modifier le backend) :
 * fieldMeta, payloadFields, items typés, payload_schema, fields/fieldDefinitions.
 */
export function enrichConditionFieldCatalog(catalog) {
    if (!catalog || typeof catalog !== "object") return catalog || {};
    const fieldMeta = { ...(catalog.fieldMeta || {}) };

    Object.entries(buildFieldMetaFromPayloadSchema(catalog.payload_schema)).forEach(([k, v]) => {
        fieldMeta[k] = mergeFieldMetaEntry(fieldMeta[k], v);
    });

    (catalog.payloadFields || []).forEach((pf) => {
        const name = pf.name || pf.path || pf.earnPointsPath || "";
        if (!name) return;
        const key = name.startsWith("payload.") ? name : `payload.${name}`;
        const options = pf.options || pf.enumValues || pf.ui?.options;
        const node = schemaNodeForPayloadField(pf, catalog);
        const fromNode = fieldMetaFromSchemaNode(node);
        fieldMeta[key] = mergeFieldMetaEntry(fieldMeta[key], {
            valueKind: pf.valueKind || fromNode.valueKind,
            type: node.type,
            format: node.format,
            label: pf.label || pf.description,
            operators: pf.operators,
            ui: pf.ui || (options ? { widget: "select", options } : fromNode.ui),
        });
    });

    (catalog.items || []).forEach((item) => {
        const id = catalogFieldId(item);
        if (!id || typeof item === "string") return;
        const options = item.options || item.enumValues;
        fieldMeta[id] = mergeFieldMetaEntry(fieldMeta[id], {
            valueKind: item.valueKind || item.value_kind,
            type: item.type,
            format: item.format,
            label: item.label,
            operators: item.operators,
            ui: item.ui || (options ? { widget: "select", options } : undefined),
        });
    });

    [catalog.fields, catalog.fieldDefinitions].filter(Array.isArray).forEach((list) => {
        list.forEach((item) => {
            const id = catalogFieldId(item);
            if (!id) return;
            fieldMeta[id] = mergeFieldMetaEntry(fieldMeta[id], {
                valueKind: item.valueKind || item.value_kind,
                type: item.type,
                label: item.label,
                operators: item.operators,
                ui: item.ui,
            });
        });
    });

    return { ...catalog, fieldMeta };
}

function resolvePayloadFieldMeta(field, catalog = {}) {
    if (!field?.startsWith("payload.")) return null;
    const fullKey = field;
    const shortKey = field.replace(/^payload\./, "");

    const fromMeta = catalog?.fieldMeta?.[fullKey] || catalog?.fieldMeta?.[shortKey];
    if (fromMeta?.valueKind || fromMeta?.ui?.widget) return fromMeta;

    const pf = (catalog?.payloadFields || []).find((f) => {
        const name = f.name || f.path || f.earnPointsPath || "";
        const normalized = name.startsWith("payload.") ? name : `payload.${name}`;
        return normalized === fullKey || name === shortKey;
    });
    if (pf) {
        const options = pf.options || pf.enumValues || pf.ui?.options;
        const node = schemaNodeForPayloadField(pf, catalog);
        const fromNode = fieldMetaFromSchemaNode(node);
        return {
            valueKind: pf.valueKind || fromNode.valueKind,
            ui: pf.ui || (options ? { widget: "select", options } : fromNode.ui),
            label: pf.label || pf.description || shortKey,
            format: node.format,
        };
    }

    const schemaProp = lookupPayloadSchemaProperty(catalog, shortKey);
    if (schemaProp) return fieldMetaFromSchemaNode(schemaProp);

    const fromSchema = buildFieldMetaFromPayloadSchema(catalog?.payload_schema)[fullKey];
    if (fromSchema) return fromSchema;

    return null;
}

function resolveCatalogMeta(field, catalog = {}) {
    if (!field) return {};
    return catalog?.fieldMeta?.[field]
        || resolvePayloadFieldMeta(field, catalog)
        || buildFieldMetaFromPayloadSchema(catalog?.payload_schema)?.[field]
        || catalog?.fieldMeta?.[field.replace(/^payload\./, "")]
        || CUSTOMER_FIELD_META_FALLBACK[field]
        || {};
}

function normalizeValueKind(rawKind, meta = {}) {
    const widget = meta.ui?.widget;
    if (widget === "number") return "number";
    if (widget === "date") return "date";
    if (widget === "datetime") return "datetime";
    if (widget === "time") return "time";
    if (widget === "email") return "email";
    if (widget === "tel") return "phone";
    if (widget === "url") return "uri";
    if (widget === "select" || widget === "remote_select") return "enum";
    if (widget === "remote_multi_select") return "set";
    if (widget === "array") return "array";
    if (widget === "json") return "object";

    const k = String(rawKind || "").toLowerCase();
    if (["integer", "int", "float", "decimal", "number"].includes(k)) return "number";
    if (["bool", "boolean"].includes(k)) return "boolean";
    if (k === "set") return "set";
    if (k === "enum") return "enum";
    if (["date", "datetime", "time", "email", "phone", "uri", "uuid", "ipv4"].includes(k)) return k;
    if (k) return k;
    return "string";
}

function resolveInputKind(valueKind, meta = {}) {
    const normalized = normalizeValueKind(valueKind, meta);
    const widget = meta.ui?.widget;
    if (widget === "number" || normalized === "number") return "number";
    if (widget === "date" || normalized === "date") return "date";
    if (widget === "datetime" || normalized === "datetime") return "datetime";
    if (widget === "time" || normalized === "time") return "time";
    if (widget === "email" || normalized === "email") return "email";
    if (widget === "tel" || normalized === "phone") return "phone";
    if (widget === "url" || normalized === "uri") return "uri";
    if (widget === "select" || widget === "remote_select" || normalized === "enum") return "enum";
    if (normalized === "boolean") return "boolean";
    if (normalized === "set") return "set";
    if (normalized === "uuid" || normalized === "ipv4") return normalized;
    if (normalized === "array") return "array";
    if (normalized === "object") return "object";
    return normalized || "string";
}

function inferLoyaltyValueKind(field, meta = {}) {
    if (field === BIRTHDATE_FIELD) return "birthdate";
    if (LOYALTY_SPECIAL[field]) return LOYALTY_SPECIAL[field];
    if (meta.valueKind || meta.ui?.widget) return normalizeValueKind(meta.valueKind, meta);
    if (meta.format) {
        const fromFormat = schemaNodeToValueKind({ type: "string", format: meta.format });
        if (fromFormat !== "string") return fromFormat;
    }
    const fromName = inferValueKindFromFieldName(field);
    if (fromName) return fromName;
    if (LOYALTY_ENUM_FIELDS[field]) return "enum";
    if (LOYALTY_NUMERIC_FIELDS.has(field) || LOYALTY_NUMERIC_PATTERNS.some((p) => p.test(field))) return "number";
    if (LOYALTY_DATE_FIELDS.has(field)) return "datetime";
    if (field?.includes("date") || field?.includes("_at")) return "date";
    return "string";
}

function resolveOperatorPoolKey(descriptor) {
    if (descriptor?.isBirthdate) return "birthdate";
    if (descriptor?.special === "loyalty_tier") return "loyalty_tier";
    if (descriptor?.special === "reward" || descriptor?.valueKind === "set") return "reward";
    const kind = descriptor?.inputKind || descriptor?.valueKind || "string";
    if (STRING_LIKE_KINDS.has(kind)) return "string";
    return kind;
}

export function resolveFieldDescriptor(field, catalog = {}, dialect = "loyalty", unomiField = null) {
    const meta = resolveCatalogMeta(field, catalog);
    if (dialect === "unomi") {
        const propId = field || unomiField?.id || "";
        const type = unomiField?.type || fieldTypeForProperty(propId);
        const valueKind = type === "number" ? "number" : type === "date" ? "date" : type === "boolean" ? "boolean" : "string";
        return {
            field: propId,
            dialect,
            valueKind,
            inputKind: valueKind,
            ui: {},
            isBirthdate: propId === "properties.birthDate",
            label: unomiField?.label || propId.replace("properties.", ""),
        };
    }

    const valueKind = inferLoyaltyValueKind(field, meta);
    let special = LOYALTY_SPECIAL[field] || null;
    if (meta.valueKind === "set" || meta.ui?.widget === "remote_multi_select") special = "reward";
    if (meta.ui?.widget === "remote_select" && field?.includes("loyalty_status")) special = "loyalty_tier";

    const inputKind = resolveInputKind(valueKind, meta);
    return {
        field,
        dialect,
        valueKind,
        inputKind,
        ui: meta.ui || {},
        enumOptions: meta.ui?.options || LOYALTY_ENUM_FIELDS[field] || [],
        itemsKind: meta.ui?.itemsKind || "string",
        operators: Array.isArray(meta.operators) ? meta.operators : null,
        isBirthdate: valueKind === "birthdate",
        special,
        label: meta.label || (field?.includes(".") ? field.split(".").slice(1).join(".") : field),
        isPayload: field?.startsWith("payload."),
    };
}

export function getOperatorLabel(op, dialect = "loyalty") {
    if (dialect === "unomi") return UNOMI_OP_LABELS[op] || op;
    return LOYALTY_OP_LABELS[op] || op;
}

export function filterOperatorsForField(descriptor, allOperators, dialect = "loyalty") {
    if (Array.isArray(descriptor?.operators) && descriptor.operators.length > 0) {
        const fromApi = new Set(descriptor.operators);
        const filtered = (allOperators || []).filter((op) => fromApi.has(op));
        return filtered.length > 0 ? filtered : descriptor.operators;
    }
    const pool = dialect === "unomi" ? UNOMI_OPS_BY_KIND : LOYALTY_OPS_BY_KIND;
    const kind = resolveOperatorPoolKey(descriptor);
    const allowed = new Set(pool[kind] || pool.string);
    const filtered = (allOperators || []).filter((op) => allowed.has(op));
    return filtered.length > 0 ? filtered : (allOperators || []);
}

export function resolveAllowedOperators(field, allOperators, catalog = {}, dialect = "loyalty", unomiField = null) {
    const descriptor = resolveFieldDescriptor(field, catalog, dialect, unomiField);
    return filterOperatorsForField(descriptor, allOperators, dialect);
}

export function operatorsForSelect(field, allOperators, catalog = {}, dialect = "loyalty", unomiField = null, currentOp = "") {
    const allowed = field
        ? resolveAllowedOperators(field, allOperators, catalog, dialect, unomiField)
        : (allOperators || []);
    if (currentOp && !allowed.includes(currentOp)) return [currentOp, ...allowed];
    return allowed;
}

export function defaultValueForFieldOperator(descriptor, operator) {
    if (operator === "exists") return true;
    if (operator === "between") return ["", ""];
    if (operator === "in") return [];
    const kind = descriptor?.inputKind || descriptor?.valueKind;
    if (kind === "boolean") return true;
    if (kind === "number") return "";
    if (kind === "array" || kind === "object") return [];
    return "";
}

export function coerceValueForContext(descriptor, operator, prevValue) {
    if (operator === "exists") {
        return prevValue === true || prevValue === "true" ? true : false;
    }
    if (operator === "between") {
        if (Array.isArray(prevValue) && prevValue.length >= 2) return prevValue;
        return ["", ""];
    }
    if (operator === "in") {
        if (Array.isArray(prevValue)) return prevValue;
        if (prevValue != null && prevValue !== "") return [prevValue];
        return [];
    }
    if (Array.isArray(prevValue)) {
        return coerceValueForContext(descriptor, operator, prevValue[0] ?? "");
    }
    const kind = descriptor?.inputKind || descriptor?.valueKind;
    if (kind === "number") {
        const n = Number(prevValue);
        return prevValue === "" || Number.isNaN(n) ? "" : n;
    }
    if (descriptor?.isBirthdate && prevValue != null && prevValue !== "") return prevValue;
    return prevValue ?? defaultValueForFieldOperator(descriptor, operator);
}

export function resolveDefaultConditionValue(field, operator, catalog = {}, dialect = "loyalty", unomiField = null) {
    if (field === BIRTHDATE_FIELD && dialect === "loyalty") return makeBirthdateSystemToday();
    const descriptor = resolveFieldDescriptor(field, catalog, dialect, unomiField);
    return defaultValueForFieldOperator(descriptor, operator);
}

export function applyFieldChange(prev, newField, allOperators, catalog = {}, dialect = "loyalty", unomiField = null) {
    const allowed = resolveAllowedOperators(newField, allOperators, catalog, dialect, unomiField);
    const defaultOp = dialect === "unomi" ? "equals" : "eq";
    const operator = allowed.includes(prev.operator) ? prev.operator : (allowed[0] || defaultOp);
    const value = resolveDefaultConditionValue(newField, operator, catalog, dialect, unomiField);
    if (dialect === "unomi") {
        return { ...prev, property: newField, operator, value };
    }
    return { ...prev, field: newField, operator, value };
}

export function applyOperatorChange(prev, newOperator, catalog = {}, dialect = "loyalty", unomiField = null) {
    const fieldKey = dialect === "unomi" ? (prev.property || prev.field) : prev.field;
    const descriptor = resolveFieldDescriptor(fieldKey, catalog, dialect, unomiField);
    const value = coerceValueForContext(descriptor, newOperator, prev.value);
    return { ...prev, operator: newOperator, value };
}

export function valueInputHelperText(descriptor, operator, dialect = "loyalty") {
    const kind = descriptor?.inputKind || descriptor?.valueKind;
    if (operator === "exists") return "Vérifie si le champ est renseigné (non nul).";
    if (operator === "in") {
        if (kind === "number") return "Ajoutez des nombres un par un (Entrée).";
        if (kind === "enum") return "Sélectionnez une ou plusieurs valeurs.";
        if (kind === "email") return "Ajoutez des adresses email (Entrée pour valider chaque entrée).";
        if (kind === "phone") return "Ajoutez des numéros (8 à 15 chiffres, Entrée pour valider).";
        return "Ajoutez des valeurs une par une (Entrée).";
    }
    if (operator === "between") {
        if (kind === "number") return "Intervalle numérique : valeur min et max.";
        if (kind === "date" || kind === "datetime" || kind === "time") return "Intervalle de dates ou d'heures.";
        return "Intervalle : borne basse et borne haute.";
    }
    if (kind === "number") return "Nombre attendu pour ce champ.";
    if (kind === "date") return "Date au format AAAA-MM-JJ.";
    if (kind === "datetime") return "Date et heure, ou preset dynamique.";
    if (kind === "time") return "Heure au format HH:mm.";
    if (kind === "email") return "Adresse email (ex. client@exemple.com).";
    if (kind === "phone") return "Numéro de téléphone (chiffres, +, espaces).";
    if (kind === "uri") return "URL ou URI (ex. https://…).";
    if (kind === "uuid") return "Identifiant UUID (ex. 550e8400-e29b-41d4-a716-446655440000).";
    if (kind === "ipv4") return "Adresse IPv4 (ex. 192.168.0.1).";
    if (kind === "array") {
        const itemLabel = valueKindLabel(descriptor?.itemsKind || "string").toLowerCase();
        if (operator === "contains") return `Texte recherché parmi les éléments ${itemLabel} du tableau.`;
        if (operator === "in") return `Un ou plusieurs éléments ${itemLabel} à retrouver dans le tableau.`;
        return `Liste d'éléments ${itemLabel} (Entrée pour ajouter).`;
    }
    if (kind === "object") return "Objet JSON — réservé à la vérification d'existence.";
    if (descriptor?.special === "loyalty_tier") return "Niveau de fidélité du client.";
    if (descriptor?.special === "reward") return "Récompense catalogue.";
    if (dialect === "unomi" && kind === "string") return "Texte libre — respectez la casse si nécessaire.";
    if (descriptor?.isPayload) return `Valeur conforme au type ${valueKindLabel(kind)} du champ payload.`;
    return null;
}

export { valueKindLabel };
